import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  SearchInput,
  SegmentedControl,
  SkeletonList,
  PageAction,
} from '@/components/ds';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { SubmitTicketModal } from '@/components/feedback/SubmitTicketModal';
import { statusMeta, TYPE_META } from '@/components/feedback/ticket-vocabulary';
import { useFormatters } from '@/hooks/useFormatters';
import { feedbackApi, type Ticket, type TicketType } from '@/api/feedback.api';

type StatusFilter = 'open' | 'closed' | 'all';

/**
 * The clinic's reports.
 *
 * Defaults to open items, because the question someone arrives with is "what
 * did I report that still isn't sorted" — not "show me everything I have ever
 * written". Auto-captured crashes never appear here; the server excludes them
 * from this query entirely.
 */
export function FeedbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { relative, dateTime } = useFormatters();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusFilter>('open');
  const [type, setType] = useState<TicketType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await feedbackApi.list({
        status,
        type: type === 'all' ? undefined : type,
        search: search.trim() || undefined,
      });
      setTickets(result.items);
    } catch {
      setError(t('feedback.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [status, type, search, t]);

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per
    // keystroke; the filter selects fire immediately because they are discrete.
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const hasFilters =
    search.trim() !== '' || type !== 'all' || status !== 'open';

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-4xl space-y-4">
        <PageHeader
          title={t('feedback.title')}
          description={t('feedback.subtitle')}
          actions={
            <PageAction
              icon={<Plus aria-hidden />}
              onPress={() => setModalOpen(true)}
            >
              {t('feedback.new')}
            </PageAction>
          }
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SegmentedControl<StatusFilter>
            value={status}
            onChange={setStatus}
            aria-label={t('feedback.filter.open')}
            options={[
              { value: 'open', label: t('feedback.filter.open') },
              { value: 'closed', label: t('feedback.filter.closed') },
              { value: 'all', label: t('feedback.filter.all') },
            ]}
          />
          <div className="sm:ms-auto sm:w-64">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('feedback.search')}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <TypeChip
            active={type === 'all'}
            onPress={() => setType('all')}
            label={t('feedback.filter.allTypes')}
          />
          {(Object.keys(TYPE_META) as TicketType[]).map((key) => (
            <TypeChip
              key={key}
              active={type === key}
              onPress={() => setType(key)}
              label={t(TYPE_META[key].labelKey)}
            />
          ))}
        </div>

        {loading ? (
          <SkeletonList rows={3} />
        ) : error ? (
          <EmptyState
            title={error}
            action={
              <Button variant="secondary" onPress={load}>
                {t('feedback.errors.retry')}
              </Button>
            }
          />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={MessageSquarePlus}
            title={t(
              hasFilters ? 'feedback.empty.noResults' : 'feedback.empty.title',
            )}
            description={t(
              hasFilters
                ? 'feedback.empty.noResultsDescription'
                : 'feedback.empty.description',
            )}
            action={
              hasFilters ? undefined : (
                <Button
                  onPress={() => setModalOpen(true)}
                  startIcon={<Plus className="size-4" />}
                >
                  {t('feedback.empty.action')}
                </Button>
              )
            }
          />
        ) : (
          <ul className="space-y-2">
            {tickets.map((ticket) => {
              const meta = statusMeta(ticket.status);
              const typeMeta = TYPE_META[ticket.type];
              const Icon = typeMeta?.icon;

              return (
                <li key={ticket.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/feedback/${ticket.id}`)}
                    className="w-full rounded-lg border border-border-subtle bg-surface-0 p-4 text-start outline-none transition-colors hover:bg-surface-1 focus-visible:ring-2 focus-visible:ring-primary/60"
                  >
                    <div className="flex items-start gap-3">
                      {Icon && (
                        <Icon
                          aria-hidden
                          className="mt-0.5 size-4 shrink-0 text-muted"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <span className="text-body font-semibold text-foreground">
                            {ticket.title}
                          </span>
                          {/* Status is in text inside the badge, never colour
                              alone — see the accessibility note in the brief. */}
                          <Badge tone={meta.tone}>{t(meta.labelKey)}</Badge>
                          {ticket.unreadReplies > 0 && (
                            <Badge tone="primary" withDot>
                              {t('feedback.unreadReplies', {
                                count: ticket.unreadReplies,
                              })}
                            </Badge>
                          )}
                        </div>
                        <p className="line-clamp-2 text-small text-muted">
                          {ticket.friendlySummary ?? ticket.description}
                        </p>
                        <time
                          dateTime={ticket.updatedAt}
                          title={dateTime(ticket.updatedAt)}
                          className="mt-1 block text-micro text-muted"
                        >
                          {t('feedback.updated', {
                            when: relative(ticket.updatedAt),
                          })}
                        </time>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <SubmitTicketModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={load}
      />
    </DashboardLayout>
  );
}

function TypeChip({
  active,
  onPress,
  label,
}: {
  active: boolean;
  onPress: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={active}
      className={
        active
          ? 'rounded-full bg-primary-600 px-3 py-1 text-micro font-medium text-white outline-none focus-visible:ring-2 focus-visible:ring-primary/60'
          : 'rounded-full bg-surface-1 px-3 py-1 text-micro font-medium text-muted outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/60'
      }
    >
      {label}
    </button>
  );
}
